import React, { useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

// Full-size photo viewer shared by the install guide and the "send us your door" shot
// guide: one photo at a time with its caption, arrows / keys walk the sequence, Escape
// or a click outside closes. `items` = [{ src, title, text }], `label` names the series
// ("Step" / "Shot") for the counter and the accessible name.
export default function PhotoLightbox({ items, index, onClose, onMove, label = 'Photo', testId = 'photo-lightbox' }) {
  const closeRef = useRef(null);
  const item = items[index];
  const n = items.length;
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onMove(1);
      if (e.key === 'ArrowLeft') onMove(-1);
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (closeRef.current) closeRef.current.focus();
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose, onMove]);
  if (!item) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/95 text-white flex flex-col" role="dialog" aria-modal="true" aria-label={`${label} ${index + 1}: ${item.title}`} data-testid={testId} onClick={onClose}>
      <div className="flex items-center justify-between px-4 py-3 text-sm" onClick={(e) => e.stopPropagation()}>
        <span className="font-semibold">{label} {index + 1} of {n}</span>
        <button ref={closeRef} type="button" onClick={onClose} className="inline-flex items-center gap-1 rounded-full border border-white/40 px-3 py-1.5 hover:bg-white/10" aria-label="Close">
          <X size={16} /> Close
        </button>
      </div>
      <div className="flex-1 min-h-0 flex items-center justify-center gap-2 px-2 sm:px-4">
        <button type="button" onClick={(e) => { e.stopPropagation(); onMove(-1); }} className="shrink-0 rounded-full p-2 sm:p-3 bg-white/10 hover:bg-white/20" aria-label={`Previous ${label.toLowerCase()}`} disabled={index === 0} style={{ visibility: index === 0 ? 'hidden' : 'visible' }}>
          <ChevronLeft size={24} />
        </button>
        <img key={index} src={item.src} alt={`${label} ${index + 1}: ${item.title}`} className="max-h-full max-w-full object-contain rounded-lg" style={{ maxHeight: 'calc(100vh - 170px)' }} onClick={(e) => e.stopPropagation()} />
        <button type="button" onClick={(e) => { e.stopPropagation(); onMove(1); }} className="shrink-0 rounded-full p-2 sm:p-3 bg-white/10 hover:bg-white/20" aria-label={`Next ${label.toLowerCase()}`} disabled={index === n - 1} style={{ visibility: index === n - 1 ? 'hidden' : 'visible' }}>
          <ChevronRight size={24} />
        </button>
      </div>
      <div className="px-4 py-3 max-w-3xl mx-auto text-center" onClick={(e) => e.stopPropagation()}>
        <p className="font-bold">{item.title}</p>
        {item.text && <p className="text-sm text-gray-300 mt-1">{item.text}</p>}
      </div>
    </div>
  );
}
